import React, { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Sun, Moon, Github, Linkedin, Instagram } from 'lucide-react';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Skills', href: '#skills' },
  { label: 'Projects', href: '#projects' },
  { label: 'Contact', href: '#contact' },
];

const Header: React.FC = () => {
  const [isDark, setIsDark] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    const stored = window.localStorage.getItem('theme');
    const prefersDark = window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;

    const activeDark = stored === 'dark' || (!stored && prefersDark);
    setIsDark(activeDark);
    document.documentElement.classList.toggle('dark', activeDark);
  }, []);

  // Listen for scroll to change header background
  useEffect(() => {
    const handler = () => {
      setIsScrolled(window.scrollY > 10);
    };
    handler();
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle('dark', next);
    window.localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  const handleAnchorClick = (href: string) => {
    if (!href.startsWith('#')) return;
    const id = href.slice(1);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-30 transition-all ${
        isScrolled
          ? 'backdrop-blur-xl bg-white/80 dark:bg-slate-900/80 shadow-md'
          : 'bg-transparent'
      }`}
      aria-label="Main navigation"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-2 sm:px-6 sm:py-3 lg:px-8">
        <Link to="/" className="flex items-center gap-2 sm:gap-3" aria-label="Go to home">
          <div className="flex aspect-square h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-transparent sm:h-12 sm:w-12">
            <img
              src="/media/projects/Logo1.png"
              alt="Marco Biosa logo"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-base font-semibold text-slate-900 sm:text-lg dark:text-slate-50">
              Marco Biosa
            </span>
            <span className="hidden text-sm text-slate-500 sm:block sm:text-base dark:text-slate-400">
              Junior Full-Stack Dev
            </span>
          </div>
        </Link>

        <nav className="hidden items-center gap-4 text-base font-medium text-slate-600 sm:gap-6 sm:text-lg dark:text-slate-300 md:flex">
          {navLinks.map((item) => (
            <button
              key={item.href}
              onClick={() => handleAnchorClick(item.href)}
              className="relative rounded-full px-2 py-1 transition hover:text-slate-900 hover:dark:text-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink/70"
            >
              {item.label}
            </button>
          ))}
          <NavLink
            to="/projects"
            className={({ isActive }) =>
              `rounded-full px-4 py-1.5 text-sm uppercase tracking-wide transition ${
                isActive
                  ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                  : 'border border-slate-200/80 bg-white/60 text-slate-700 hover:border-brand-pink/60 hover:text-slate-900 dark:border-slate-700/80 dark:bg-slate-900/50 dark:text-slate-200 dark:hover:border-brand-pink/70'
              }`
            }
          >
            All Projects
          </NavLink>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden items-center gap-1.5 sm:flex sm:gap-2">
            <a
              href="https://github.com/Markitos998"
              target="_blank"
              rel="noreferrer"
              aria-label="Marco on GitHub"
              className="rounded-full p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/80 dark:hover:text-slate-50"
            >
              <Github className="h-4 w-4" />
            </a>
            <a
              href="https://www.linkedin.com/in/marco-biosa-921495195/"
              target="_blank"
              rel="noreferrer"
              aria-label="Marco on LinkedIn"
              className="rounded-full p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/80 dark:hover:text-slate-50"
            >
              <Linkedin className="h-4 w-4" />
            </a>
            <a
              href="https://www.instagram.com/markquitos/"
              target="_blank"
              rel="noreferrer"
              aria-label="Marco on Instagram"
              className="rounded-full p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/80 dark:hover:text-slate-50"
            >
              <Instagram className="h-4 w-4" />
            </a>
          </div>

          <button
            type="button"
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200/80 bg-white/80 text-slate-700 shadow-sm transition hover:border-brand-pink/70 hover:text-slate-900 dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:border-brand-pink/70"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;



